/**
 * #1106 — owns the inline-image side of a TextArea field on web.
 *
 * This is the data half of the editor split: it knows the request id, the
 * request section and whether the app is online, and it turns image bytes into
 * a persisted reference. The editor control receives only the returned
 * callback, so `TextAreaEditor` / `RichTextEditor` never call the attachment
 * API or the offline queue themselves.
 */
import { useCallback } from 'react';

import {
  assertInlineImageAcceptable,
  canUploadClipboardImage,
  shouldStageInlineImage,
} from './inlineImageUploadPolicy';
import { saveAttachment } from '../../services/attachmentService';
import {
  createOfflineInlineImageClientId,
  listOfflineInlineImages,
  stageOfflineInlineImage,
} from '../../services/offlineInlineImageQueue';
import { rememberPendingInlineImagePreview } from '../../services/pendingInlineImagePreview';
import {
  beginInlineImageAttempt,
  countUnreferencedStagedInlineImages,
  endInlineImageAttempt,
  generateInlineAttachmentName,
  getInlineImageUploadProgress,
  inlineImageFieldKey,
  nextInlineImageSequence,
  setInlineImageUploadProgress,
} from '../../utils/richContent/inlineAttachmentName';

import type {
  InlineImageFile,
  InlineImageResolution,
} from './inlineImage.types';

export type InlineImageUploadContext = {
  /** Numeric persisted request id, or null before the first save (HLD §7.4). */
  requestId: number | null;
  requestSection: string;
  isOnline: boolean;
  fieldLabel: string;
  fieldHtml: string;
  /** Rich HTML transport key for create-flow attachment flush merge. */
  transportFieldKey?: string;
};

function readFileAsDataUrl(
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read image file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    if (onProgress && file.size > 0) {
      reader.onprogress = event => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    reader.readAsDataURL(file);
  });
}

async function buildInlineFileName(
  file: File,
  fieldLabel: string,
  fieldHtml: string,
  requestSection: string,
  requestId: number | null,
  extraInFlight: number,
): Promise<string> {
  const staged = await listOfflineInlineImages();
  const extra =
    countUnreferencedStagedInlineImages(fieldHtml, requestSection, staged, {
      requestId,
      fieldLabel,
    }) + extraInFlight;
  return generateInlineAttachmentName({
    fieldLabel,
    sequence: nextInlineImageSequence(fieldHtml, extra),
    mimeType: file.type,
  });
}

export function useInlineImageUpload({
  requestId,
  requestSection,
  isOnline,
  fieldLabel,
  fieldHtml,
  transportFieldKey,
}: InlineImageUploadContext): (
  file: InlineImageFile,
) => Promise<InlineImageResolution> {
  return useCallback(
    async (file: InlineImageFile): Promise<InlineImageResolution> => {
      if (!(file instanceof File)) {
        return { ok: false, error: 'Unsupported image source' };
      }

      const acceptable = assertInlineImageAcceptable(file);
      if (!acceptable.ok) {
        return { ok: false, error: acceptable.error };
      }

      const fieldKey = inlineImageFieldKey(requestSection, fieldLabel);
      const extraInFlight = beginInlineImageAttempt(fieldKey);
      let fileName = file.name || 'image';
      try {
        fileName = await buildInlineFileName(
          file,
          fieldLabel,
          fieldHtml,
          requestSection,
          requestId,
          extraInFlight,
        );
        const namedFile = new File([file], fileName, { type: file.type });
        setInlineImageUploadProgress({
          fileName,
          progress: 0,
          status: 'uploading',
        });

        if (shouldStageInlineImage({ isOnline, requestId })) {
          const clientId = createOfflineInlineImageClientId();
          const dataUrl = await readFileAsDataUrl(namedFile, progress => {
            setInlineImageUploadProgress({
              fileName,
              progress,
              status: 'uploading',
            });
          });
          const commaIndex = dataUrl.indexOf(',');
          const dataBase64 =
            commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
          try {
            await stageOfflineInlineImage({
              clientId,
              requestId,
              requestSection,
              fieldLabel,
              transportFieldKey,
              mimeType: namedFile.type,
              fileName,
              dataBase64,
              createdAt: Date.now(),
            });
          } catch {
            return {
              ok: false,
              error: 'Could not stage the image',
            };
          }
          const reference = `sitescope-attachment-pending://${clientId}`;
          rememberPendingInlineImagePreview(
            reference,
            URL.createObjectURL(namedFile),
          );
          setInlineImageUploadProgress({
            fileName,
            progress: 100,
            status: 'completed',
          });
          return {
            ok: true,
            reference,
            uploaded: false,
          };
        }

        const gate = canUploadClipboardImage({ isOnline, requestId });
        if (!gate.ok) {
          return { ok: false, error: gate.reason };
        }

        const result = await saveAttachment(
          namedFile,
          requestId,
          requestSection,
          'inline',
          undefined,
          undefined,
          undefined,
          progress => {
            setInlineImageUploadProgress({
              fileName,
              progress,
              status: 'uploading',
            });
          },
        );
        const attachmentId = result.attachment?.id;
        if (!result.success || !attachmentId) {
          return {
            ok: false,
            error: result.error || 'Attachment upload failed',
          };
        }
        setInlineImageUploadProgress({
          fileName,
          progress: 100,
          status: 'completed',
        });
        return {
          ok: true,
          reference: `sitescope-attachment://${attachmentId}`,
          attachmentId,
          uploaded: true,
        };
      } finally {
        endInlineImageAttempt(fieldKey);
        if (getInlineImageUploadProgress()?.status === 'completed') {
          setTimeout(() => setInlineImageUploadProgress(null), 600);
        } else {
          setInlineImageUploadProgress(null);
        }
      }
    },
    [
      fieldHtml,
      fieldLabel,
      isOnline,
      requestId,
      requestSection,
      transportFieldKey,
    ],
  );
}
